import React, { useState } from "react";
import styled from "@emotion/styled";
import {
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  zIndex,
  border as borderToken,
  brand,
  interactive,
  surface,
  text,
} from "@atlas-int/tokens";
import PreviewSection from "../components/PreviewSection";
import CodeBlock from "../components/CodeBlock";
import { TreeSelect, type TreeNode, type FlatItem } from "@atlas-int/ui";

// ─── Styled helpers ──────────────────────────────────────────────────────────

const DemoRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing[8]};
  align-items: flex-start;
`;

const DemoGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const DemoLabel = styled.div`
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  color: ${text.secondary};
  margin-bottom: ${spacing[2]};
`;

const SubSection = styled.div`
  margin-bottom: ${spacing[12]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SubTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const Description = styled.p`
  font-size: ${fontSize.base};
  color: ${text.secondary};
  margin: 0 0 ${spacing[6]} 0;
`;

const DemoBox = styled.div<{ $width?: string }>`
  width: ${({ $width }) => $width ?? "300px"};
  position: relative;
`;

const Note = styled.div`
  font-size: ${fontSize.sm};
  color: ${text.muted};
  padding: ${spacing[4]} ${spacing[6]};
  background-color: ${surface.muted};
  border-radius: ${borderRadius.base};
  margin-top: ${spacing[6]};
`;

// ─── Visual replicas for hook-dependent controls ────────────────────────────

// RcaSlider replica (original uses usePageQueryParams)
const SliderInput = styled.input`
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  padding: ${spacing[2]};

  ::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    background-color: ${interactive.default};
    cursor: pointer;
    border-radius: ${borderRadius.full};
    border: 1px solid white;
    margin-top: -3px;
  }

  ::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background-color: ${interactive.default};
    cursor: pointer;
    border: 1px solid white;
    margin-top: -3px;
  }

  ::-webkit-slider-runnable-track {
    border: 1px solid white;
    background: linear-gradient(
      to right,
      ${interactive.default} 0%,
      ${interactive.default} var(--value),
      #ffff var(--value),
      #ffff 100%
    );
    height: 12px;
    border-radius: ${borderRadius.md};
    border: 1px solid ${borderToken.control};
  }

  ::-moz-range-track {
    border: 1px solid white;
    background: linear-gradient(
      to right,
      ${interactive.default} 0%,
      ${interactive.default} var(--value),
      #ffff var(--value),
      #ffff 100%
    );
    height: 12px;
    border-radius: ${borderRadius.md};
    border: 1px solid ${borderToken.control};
  }
`;

// SelectBox replica styled components (same as vizSettingsDropdown/styles)
const SelectBoxContainer = styled.div`
  width: 100%;
  position: relative;
`;

const SelectDropdownContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  height: 34px;
  line-height: 1.15;
  box-sizing: border-box;
  cursor: pointer;
  padding: ${spacing[0]};
  border: 1px solid ${borderToken.control};
  border-radius: ${borderRadius.base};
`;

const SelectLabel = styled.div`
  width: 100%;
  padding: 1px ${spacing[1]} 1px ${spacing[2]};
  box-sizing: border-box;
  display: inline-block;
  margin: auto ${spacing[0]};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const SelectMenu = styled.ul<{ $open?: boolean }>`
  max-height: 200px;
  overflow-y: auto;
  position: absolute;
  z-index: ${zIndex.dropdown};
  background-color: ${surface.raised};
  list-style-type: none;
  padding: 0;
  margin: 0;
  width: 100%;
  box-sizing: border-box;
  display: ${({ $open }) => ($open ? "block" : "none")};
  border: 1px solid ${borderToken.control};
  border-top: none;
  border-bottom-right-radius: ${borderRadius.base};
  border-bottom-left-radius: ${borderRadius.base};
`;

const SelectMenuItem = styled.li<{ $highlighted?: boolean }>`
  height: 36px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: ${text.primary};
  box-sizing: border-box;
  padding: 0 ${spacing[4]};
  background-color: ${({ $highlighted }) => ($highlighted ? surface.panel : surface.raised)};

  &:hover {
    background-color: ${surface.panel};
  }
`;

const SelectArrow = styled.span`
  padding-right: ${spacing[2]};
  display: inline-block;
  margin: auto ${spacing[0]};
  font-size: 10px;
  color: ${text.muted};
`;

// ShowTable replica (original uses useLocation/useNavigate)
const TableToggleButton = styled.button<{ $active?: boolean }>`
  border: 1px solid ${text.primary};
  margin: ${spacing[0]};
  padding-left: ${spacing[6]};
  padding-right: ${spacing[6]};
  height: 34px;
  cursor: pointer;
  background-color: ${({ $active }) => ($active ? interactive.default : surface.raised)};
  color: ${({ $active }) => ($active ? text.onDark : text.primary)};
  letter-spacing: 0.2px;

  &:hover {
    background-color: ${brand.dark};
    color: ${text.onDark};
  }
`;

const LeftToggle = styled(TableToggleButton)`
  border-top-left-radius: ${borderRadius.base};
  border-bottom-left-radius: ${borderRadius.base};
  border-right: 0px;
`;

const RightToggle = styled(TableToggleButton)`
  border-bottom-right-radius: ${borderRadius.base};
  border-top-right-radius: ${borderRadius.base};
  border-left: 0px;
`;

// ─── RcaSlider Section ──────────────────────────────────────────────────────

const RcaSliderDemo: React.FC = () => {
  const [value, setValue] = useState(1);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    setValue(v);
    e.target.style.setProperty("--value", `${(v / 5) * 100}%`);
  };

  return (
    <SubSection>
      <SubTitle>RcaSlider</SubTitle>
      <Description>
        Range slider for filtering by Revealed Comparative Advantage threshold (0–5).
        Used in the feasibility/product space visualizations.
      </Description>
      <DemoRow>
        <DemoGroup>
          <DemoLabel>RCA threshold slider</DemoLabel>
          <DemoBox>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <SliderInput
                min={0}
                max={5}
                step={0.1}
                value={value}
                onChange={handleChange}
                type="range"
                style={{ "--value": `${(value / 5) * 100}%` } as React.CSSProperties}
              />
              <div style={{ display: "flex" }}>
                <div>RCA=</div>
                <input
                  style={{ width: "35px" }}
                  type="number"
                  value={value}
                  onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
                  step={0.1}
                  min={0}
                  max={5}
                />
              </div>
            </div>
          </DemoBox>
        </DemoGroup>
      </DemoRow>
      <CodeBlock
        code={`import RcaSlider from "components/controls/RcaSlider";

// Inside a page with query param context:
<RcaSlider />

// The slider renders a range input (0–5, step 0.1)
// with a numeric input showing the current value.
// State is managed via the rcaThreshold query param.`}
      />
    </SubSection>
  );
};

// ─── SelectBoxPrimitive Section ─────────────────────────────────────────────

const mockSelectItems = [
  { value: "sector", label: "Sector" },
  { value: "complexity", label: "Complexity" },
  { value: "cluster", label: "Cluster" },
];

const SelectBoxDemo: React.FC = () => {
  const [selected, setSelected] = useState(mockSelectItems[0]);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <SubSection>
      <SubTitle>SelectBoxPrimitive</SubTitle>
      <Description>
        Dropdown select built on Downshift's <code>useSelect</code> hook. Used in the
        visualization settings panel for options like ColorBy, ProductLevel, and YAxis.
      </Description>
      <DemoRow>
        <DemoGroup>
          <DemoLabel>Color by dropdown</DemoLabel>
          <DemoBox $width="200px">
            <SelectBoxContainer>
              <SelectDropdownContainer
                onClick={() => setIsOpen(!isOpen)}
                style={isOpen ? { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } : {}}
              >
                <SelectLabel>{selected.label}</SelectLabel>
                <SelectArrow>{isOpen ? "▲" : "▼"}</SelectArrow>
              </SelectDropdownContainer>
              <SelectMenu $open={isOpen}>
                {mockSelectItems.map((item) => (
                  <SelectMenuItem
                    key={item.value}
                    $highlighted={item.value === selected.value}
                    onClick={() => {
                      setSelected(item);
                      setIsOpen(false);
                    }}
                  >
                    {item.label}
                  </SelectMenuItem>
                ))}
              </SelectMenu>
            </SelectBoxContainer>
          </DemoBox>
        </DemoGroup>
      </DemoRow>
      <CodeBlock
        code={`import { SelectBoxPrimitive, SettingsDropdownItem } from "@atlas-int/controls";

<SelectBoxPrimitive
  items={[{ value: "sector", label: "Sector" }, { value: "complexity", label: "Complexity" }]}
  hashFunction={(item) => item.label}
  ItemRenderComponent={SettingsDropdownItem}
  onChangeEvent={({ value }) => setColorBy(value)}
  matchSelectedItem={currentColorBy}
/>`}
      />
    </SubSection>
  );
};

// ─── ShowTable Section ──────────────────────────────────────────────────────

const ShowTableDemo: React.FC = () => {
  const [showTable, setShowTable] = useState(false);

  return (
    <SubSection>
      <SubTitle>ShowTable</SubTitle>
      <Description>
        Toggle button pair for switching between Graph View and Table View.
        Used on the feasibility page.
      </Description>
      <DemoRow>
        <DemoGroup>
          <DemoLabel>View toggle</DemoLabel>
          <div>
            <LeftToggle $active={!showTable} onClick={() => setShowTable(false)}>
              Graph View
            </LeftToggle>
            <RightToggle $active={showTable} onClick={() => setShowTable(true)}>
              Table View
            </RightToggle>
          </div>
        </DemoGroup>
      </DemoRow>
      <CodeBlock
        code={`import ShowTable from "components/controls/ShowTable";

// Renders two styled buttons: "Graph View" | "Table View"
// Active state is derived from the current URL path.
<ShowTable />`}
      />
    </SubSection>
  );
};

// ─── TreeSelect Section ─────────────────────────────────────────────────

interface MockTreeItem extends FlatItem {
  name_short_en: string;
  parentId?: string;
}

const mockTreeItems: MockTreeItem[] = [
  { id: "all", name_short_en: "All Items" },
  { id: "fruits", name_short_en: "Fruits" },
  { id: "apple", name_short_en: "Apple", parentId: "fruits" },
  { id: "banana", name_short_en: "Banana", parentId: "fruits" },
  { id: "cherry", name_short_en: "Cherry", parentId: "fruits" },
  { id: "vegetables", name_short_en: "Vegetables" },
  { id: "carrot", name_short_en: "Carrot", parentId: "vegetables" },
  { id: "broccoli", name_short_en: "Broccoli", parentId: "vegetables" },
  { id: "spinach", name_short_en: "Spinach", parentId: "vegetables" },
];

const buildMockTree = (items: MockTreeItem[]): TreeNode[] => {
  const topLevel = items.filter((i) => !i.parentId);
  return topLevel.map((item) => ({
    id: item.id,
    name: item.name_short_en,
    data: item,
    children: items
      .filter((child) => child.parentId === item.id)
      .map((child) => ({ id: child.id, name: child.name_short_en, data: child })),
  }));
};

const MockTreeRenderItem = ({
  node,
  isHighlighted,
  isSelected,
  hasChildren,
  isExpanded,
  level,
  onToggle,
  onClick,
}: any) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      height: "32px",
      cursor: "pointer",
      fontSize: "0.875rem",
      color: text.primary,
      backgroundColor: isHighlighted || isSelected ? surface.panel : "transparent",
      paddingLeft: `${level * 16 + 8}px`,
    }}
    onClick={onClick}
  >
    {hasChildren && (
      <span
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        style={{ marginRight: "4px", cursor: "pointer" }}
      >
        {isExpanded ? "▾" : "▸"}
      </span>
    )}
    {node.name}
  </div>
);

const TreeDropdownDemo: React.FC = () => {
  const [selected, setSelected] = useState<MockTreeItem | undefined>(undefined);

  return (
    <SubSection>
      <SubTitle>TreeSelect</SubTitle>
      <Description>
        Virtualized tree dropdown built on TanStack Virtual and atlas-ui primitives.
        Used for the Location and Product selectors. Supports hierarchical data,
        keyboard navigation, and sticky top-level items.
      </Description>
      <DemoRow>
        <DemoGroup>
          <DemoLabel>Tree dropdown</DemoLabel>
          <DemoBox $width="275px">
            <TreeSelect
              items={mockTreeItems}
              buildTree={buildMockTree}
              value={selected}
              onChange={(item) => setSelected(item as MockTreeItem)}
              placeholder="Search items..."
              getLabel={(item) => item.name_short_en}
              searchFilter={(item, term) =>
                item.name_short_en.toLowerCase().includes(term)
              }
              renderItem={(props) => <MockTreeRenderItem {...props} />}
              estimatedItemHeight={32}
              defaultExpandedIds={new Set(["fruits", "vegetables"])}
            />
          </DemoBox>
        </DemoGroup>
      </DemoRow>
      <CodeBlock
        code={`import { TreeSelect } from "@atlas-int/ui";

<TreeSelect
  items={flatItems}
  buildTree={buildTree}
  value={selected}
  onChange={handleSelect}
  placeholder="Search..."
  getLabel={(item) => item.name}
  searchFilter={(item, term) => item.name.toLowerCase().includes(term)}
  renderItem={CustomRenderItem}
  estimatedItemHeight={32}
  defaultExpandedIds={expandedIds}
/>

// Used by LocationDropdown and ProductDropdown
// No MUI dependency — uses atlas-ui primitives
// Responds to light/dark theme toggle`}
      />
    </SubSection>
  );
};

// ─── FindInViz Section ──────────────────────────────────────────────────────

const FindInVizDemo: React.FC = () => (
  <SubSection>
    <SubTitle>FindInViz</SubTitle>
    <Description>
      Search control for highlighting items within a visualization. Shows a
      magnifying glass icon that expands into a search dropdown. Hidden for Geo,
      MarketShare, and table views.
    </Description>
    <Note>
      FindInViz depends on <code>useLocation</code> (react-router) to detect the
      current viz type and on parent-provided options/callbacks. Cannot render
      standalone.
    </Note>
    <CodeBlock
      code={`import FindInViz from "components/controls/findInViz/FindInViz";

interface FindInVizOption {
  id: string;
  label: string;
}

<FindInViz
  options={vizOptions}
  highlightedItem={highlighted}
  onHighlight={(id) => setHighlighted(id)}
/>

// Hidden automatically for Geo, MarketShare, and table views.
// Expands from icon → dropdown on hover/click.`}
    />
  </SubSection>
);

// ─── LocationDropdown / ProductDropdown Section ─────────────────────────────

const DropdownDocsDemo: React.FC = () => (
  <SubSection>
    <SubTitle>LocationDropdown &amp; ProductDropdown</SubTitle>
    <Description>
      Wrappers around <code>TreeSelect</code> that connect to the
      application data context. LocationDropdown picks countries/regions;
      ProductDropdown picks HS/SITC product codes. Both use the same virtualized
      tree primitive shown above.
    </Description>
    <Note>
      These components depend on Apollo Client data queries and{" "}
      <code>usePageQueryParams</code> for URL state. Cannot render standalone —
      see the TreeSelect demo above for the underlying primitive.
    </Note>
    <CodeBlock
      code={`import LocationDropdown from "components/controls/locationDropdown/LocationDropdown";
import ProductDropdown from "components/controls/productDropdown/ProductDropdown";

// Both render a TreeSelect with app-specific data:
<LocationDropdown />   // picks country/region
<ProductDropdown />    // picks HS/SITC product code

// Data flows:
//   GraphQL query → flat items → buildTreeStructure → TreeSelect
//   Selection → updates URL query params via usePageQueryParams`}
    />
  </SubSection>
);

// ─── Control Panels Section ─────────────────────────────────────────────────

const ControlPanelsDemo: React.FC = () => (
  <SubSection>
    <SubTitle>Control Panels</SubTitle>
    <Description>
      Composite control panels that compose dropdowns, sliders, and toggles for
      each visualization type. Four variants exist: StandardControlPanel,
      MarketsControlPanel, ProductsControlPanel, and
      SpecializedVisualizationControlPanel.
    </Description>
    <Note>
      Control panels depend on routing context and query parameters. They
      compose the primitives showcased above (TreeSelect, SelectBox,
      RcaSlider, etc.).
    </Note>
    <CodeBlock
      code={`import StandardControlPanel from "components/controls/panels/StandardControlPanel";
import MarketsControlPanel from "components/controls/panels/MarketsControlPanel";
import ProductsControlPanel from "components/controls/panels/ProductsControlPanel";
import SpecializedVisualizationControlPanel from "components/controls/panels/SpecializedVisualizationControlPanel";

// StandardControlPanel — used on most country-level viz pages
// MarketsControlPanel — bilateral trade views
// ProductsControlPanel — product-level views
// SpecializedVisualizationControlPanel — feasibility, product space`}
    />
  </SubSection>
);

// ─── Main Section ────────────────────────────────────────────────────────────

const ControlsSection: React.FC = () => (
  <>
    <PreviewSection
      id="rca-slider"
      title="RcaSlider"
      description="Range slider for RCA threshold"
    >
      <RcaSliderDemo />
    </PreviewSection>

    <PreviewSection
      id="select-box"
      title="SelectBox"
      description="Settings dropdown primitive"
    >
      <SelectBoxDemo />
    </PreviewSection>

    <PreviewSection
      id="show-table"
      title="ShowTable"
      description="Graph/Table view toggle"
    >
      <ShowTableDemo />
    </PreviewSection>

    <PreviewSection
      id="tree-dropdown"
      title="TreeSelect"
      description="Virtualized tree dropdown (Location/Product)"
    >
      <TreeDropdownDemo />
    </PreviewSection>

    <PreviewSection
      id="find-in-viz"
      title="FindInViz"
      description="Search/filter control for visualizations"
    >
      <FindInVizDemo />
    </PreviewSection>

    <PreviewSection
      id="location-product-dropdowns"
      title="LocationDropdown &amp; ProductDropdown"
      description="Data-connected tree dropdowns for location and product selection"
    >
      <DropdownDocsDemo />
    </PreviewSection>

    <PreviewSection
      id="control-panels"
      title="Control Panels"
      description="Composite control panels for each visualization type"
    >
      <ControlPanelsDemo />
    </PreviewSection>
  </>
);

export default ControlsSection;
